import React, { useEffect, useState } from "react";
import { AccountLink } from "../components/resellers/AccountLink";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { fmtDate, fmtNum } from "../lib/format";
import { ArrowLeft, Send, PackageCheck, Trash2, FileMinus, Loader2, FileDown, Pencil, Undo2 } from "lucide-react";
import { pdf } from "@react-pdf/renderer";
import CreditNotePdf from "../components/CreditNotePdf";
import { showPdfPreview } from "../components/PdfPreview";
import { CreditNoteLinesEditor } from "../components/credit-notes/CreditNoteLinesEditor";
import { CreditNoteSettlement } from "../components/credit-notes/CreditNoteSettlement";
import { CreditNoteReasonEditor } from "../components/credit-notes/CreditNoteReasonEditor";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useConfirm } from "../components/ConfirmDialog";
import { toast } from "sonner";
import { REASON_LABEL } from "../components/CreditNotePdf";

/**
 * Phase 101fk (Feb 2026) — Détail d'un avoir (NC).
 *
 * Workflow :
 * - BROUILLON : édition libre + bouton « Marquer envoyée »
 * - ENVOYEE   : peut encore être exécutée (restock) ou annulée
 * - EXECUTEE  : verrouillé (restock effectif), seule l'annulation reverse
 * - ANNULEE   : lecture seule
 */

const STATUS_META = {
  BROUILLON: { label: "Brouillon", color: "bg-[#F0EBD8] text-[#6B6B4E] border-[#D4C97A]" },
  ENVOYEE: { label: "Envoyée", color: "bg-[#E8DAD2] text-[#8B3A1F] border-[#C08363]" },
  EXECUTEE: { label: "Exécutée", color: "bg-[#DAE5D7] text-[#3E5C34] border-[#7FA070]" },
  ANNULEE: { label: "Annulée", color: "bg-[#EEE] text-[#6B6E6B] border-[#C5C6C1] line-through" },
};


export default function CreditNoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [nc, setNc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [editing, setEditing] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/credit-notes/${id}`);
      setNc(data);
      setNotes(data?.notes || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const markSent = async () => {
    setBusy(true);
    try {
      await api.put(`/credit-notes/${id}`, { status: "ENVOYEE", notes });
      toast.success("Avoir marqué comme envoyé");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const backToDraft = async () => {
    setBusy(true);
    try {
      await api.put(`/credit-notes/${id}`, { status: "BROUILLON" });
      toast.success("Avoir repassé en brouillon");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const saveLines = async (lines) => {
    setBusy(true);
    try {
      await api.put(`/credit-notes/${id}`, { lines });
      toast.success("Lignes enregistrées");
      setEditing(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const openPdf = async () => {
    setPdfBusy(true);
    try {
      const { data: settings } = await api.get("/settings").catch(() => ({ data: null }));
      const blob = await pdf(<CreditNotePdf nc={nc} settings={settings} />).toBlob();
      showPdfPreview(blob, `Avoir-${nc.reference}.pdf`);
    } catch (e) {
      toast.error(`Erreur PDF : ${(e?.message || String(e)).slice(0, 160)}`);
    } finally { setPdfBusy(false); }
  };

  const executeRestock = async () => {
    const ok = await confirm({
      title: "Exécuter la remise en stock ?",
      description: (
        <div className="text-sm">
          Cette action crée les lots au dépôt central Conthey et enregistre les mouvements HACCP.
          <br /><strong>Irréversible sans annulation de l'avoir</strong>.
        </div>
      ),
      confirmLabel: "Exécuter",
      variant: "primary",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/credit-notes/${id}/execute-restock`);
      toast.success(`Restock exécuté : ${data.restocked_lines_count} lot(s) créé(s) au dépôt Conthey`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    const ok = await confirm({
      title: "Annuler l'avoir ?",
      description: nc?.status === "EXECUTEE"
        ? "L'avoir a été exécuté (restock). L'annulation créera les mouvements OUT de reversal."
        : "L'avoir passera en statut ANNULEE.",
      confirmLabel: "Annuler l'avoir",
      variant: "danger",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/credit-notes/${id}`);
      toast.success("Avoir annulé");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const purge = async () => {
    const ok = await confirm({
      title: `Supprimer le brouillon ${nc?.reference} ?`,
      description: "Suppression définitive (brouillon jamais émis).",
      confirmLabel: "Supprimer",
      variant: "danger",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/credit-notes/${id}/purge`);
      toast.success("Brouillon supprimé");
      navigate("/notes-de-credit");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-[#575A57]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Chargement…</div>;
  }
  if (!nc) return <div className="p-8 text-center text-red-600">Avoir introuvable</div>;

  const meta = STATUS_META[nc.status] || STATUS_META.BROUILLON;
  const canSend = nc.status === "BROUILLON";
  const canExecute = nc.status === "ENVOYEE" || nc.status === "BROUILLON";
  const isSettled = (nc.applied_total || 0) > 0 || (nc.refunded_amount || 0) > 0;
  const canCancel = !["ANNULEE", "BROUILLON"].includes(nc.status) && !isSettled;
  const canBackToDraft = nc.status === "ENVOYEE" && !isSettled;
  const hasRestock = (nc.lines || []).some((ln) => ln.restock);

  return (
    <div className="space-y-6" data-testid="credit-note-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/notes-de-credit" className="inline-flex items-center gap-1 text-xs text-[#575A57] hover:text-[#1A1C1A]">
            <ArrowLeft className="w-3 h-3" /> Retour aux avoirs
          </Link>
          <h1 className="font-heading text-2xl text-[#1A1C1A] font-semibold flex items-center gap-2 mt-1">
            <FileMinus className="w-6 h-6 text-[#8B3A1F]" />
            Avoir <span className="font-mono">{nc.reference}</span>
            <Badge className={`${meta.color} text-[10px] font-mono ml-2`}>{meta.label}</Badge>
          </h1>
          <p className="text-xs text-[#575A57] mt-1">
            Émis le {fmtDate(nc.issue_date)}
            {nc.source_invoice_reference && (
              <> · Depuis facture <Link to={nc.source_invoice_id ? `/factures/${nc.source_invoice_id}` : "/factures"} className="underline">{nc.source_invoice_reference}</Link></>
            )}
            {nc.executed_at && <> · Exécuté le {fmtDate(nc.executed_at)}</>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={openPdf} disabled={pdfBusy} variant="outline" data-testid="cn-pdf">
            {pdfBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />} PDF / Imprimer
          </Button>
          {canBackToDraft && (
            <Button onClick={backToDraft} disabled={busy} variant="outline" data-testid="cn-back-to-draft">
              <Undo2 className="w-4 h-4 mr-1.5" /> Repasser en brouillon
            </Button>
          )}
          {canSend && (
            <Button onClick={markSent} disabled={busy} variant="outline" data-testid="cn-mark-sent">
              <Send className="w-4 h-4 mr-1.5" /> Marquer envoyée
            </Button>
          )}
          {canExecute && hasRestock && (
            <Button onClick={executeRestock} disabled={busy} className="bg-[#3E5C34] hover:bg-[#2E4C25] text-white" data-testid="cn-execute-restock">
              <PackageCheck className="w-4 h-4 mr-1.5" /> Exécuter restock
            </Button>
          )}
          {nc.status === "BROUILLON" && (
            <Button onClick={purge} disabled={busy} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="cn-purge">
              <Trash2 className="w-4 h-4 mr-1.5" /> Supprimer le brouillon
            </Button>
          )}
          {canCancel && (
            <Button onClick={cancel} disabled={busy} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="cn-cancel">
              <Trash2 className="w-4 h-4 mr-1.5" /> Annuler
            </Button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="md:col-span-2">
          <CardContent className="p-5 space-y-4">
            <div>
              <p className="text-xs text-[#575A57] uppercase tracking-wide mb-1">Débiteur</p>
              <p className="font-medium"><AccountLink resellerId={nc.reseller_id} distributorId={nc.distributor_id} testid="cn-detail-account">{nc.debtor_name}</AccountLink></p>
              {nc.debtor_contact_name && <p className="text-sm text-[#575A57]">{nc.debtor_contact_name}</p>}
              <p className="text-sm text-[#575A57]">{nc.debtor_address}</p>
              <p className="text-sm text-[#575A57]">{nc.debtor_postal_code} {nc.debtor_city}</p>
            </div>
            <div>
              <p className="text-xs text-[#575A57] uppercase tracking-wide mb-1">Motif</p>
              {nc.status === "BROUILLON" ? (
                <CreditNoteReasonEditor key={`${nc.reason}-${nc.reason_detail}`} nc={nc} onSaved={load} />
              ) : (
                <>
                  <p className="font-medium">{REASON_LABEL[nc.reason] || nc.reason}</p>
                  {nc.reason_detail && <p className="text-sm text-[#575A57] italic">{nc.reason_detail}</p>}
                </>
              )}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-xs text-[#575A57] uppercase tracking-wide mb-3">Totaux</p>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-[#575A57]">HT</span><span className="font-mono">{fmtNum(nc.total_ht)} CHF</span></div>
              <div className="flex justify-between"><span className="text-[#575A57]">TVA</span><span className="font-mono">{fmtNum(nc.total_vat)} CHF</span></div>
              {nc.rounding_adjustment !== 0 && (
                <div className="flex justify-between text-xs"><span className="text-[#575A57]">Arrondi</span><span className="font-mono">{fmtNum(nc.rounding_adjustment)} CHF</span></div>
              )}
              <div className="flex justify-between pt-2 border-t border-[#E5E5E0] font-semibold text-[#8B3A1F]">
                <span>Total avoir</span>
                <span className="font-mono">− {fmtNum(nc.total_ttc)} CHF</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-[#575A57] uppercase tracking-wide">Lignes de l'avoir</p>
            {canSend && !editing && (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="cn-edit-lines">
                <Pencil className="w-4 h-4 mr-1.5" /> Modifier les lignes
              </Button>
            )}
          </div>
          {editing ? (
            <CreditNoteLinesEditor initialLines={nc.lines} onSave={saveLines} onCancel={() => setEditing(false)} busy={busy} />
          ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-[#FAFAF7]">
                <TableHead>Description</TableHead>
                <TableHead className="w-20 text-right">Qté</TableHead>
                <TableHead className="w-24 text-right">P.U. HT</TableHead>
                <TableHead className="w-16 text-center">TVA</TableHead>
                <TableHead className="w-28 text-right">Sous-total</TableHead>
                <TableHead className="w-20 text-center">Restock</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(nc.lines || []).map((ln, idx) => (
                <TableRow key={ln.id || idx}>
                  <TableCell>
                    <div className="font-medium">{ln.description}</div>
                    {ln.product_sku && <div className="text-[10px] font-mono text-[#575A57]">{ln.product_sku}</div>}
                  </TableCell>
                  <TableCell className="text-right font-mono">{fmtNum(ln.quantity)} {ln.unit}</TableCell>
                  <TableCell className="text-right font-mono">{fmtNum(ln.unit_price)}</TableCell>
                  <TableCell className="text-center text-xs">{fmtNum(ln.vat_rate)}%</TableCell>
                  <TableCell className="text-right font-mono">{fmtNum(ln.quantity * ln.unit_price)}</TableCell>
                  <TableCell className="text-center">
                    {ln.restock ? (
                      <Badge className="bg-[#DAE5D7] text-[#3E5C34] text-[10px]">Oui</Badge>
                    ) : (
                      <span className="text-[10px] text-[#575A57]">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
        </CardContent>
      </Card>

      <CreditNoteSettlement nc={nc} onDone={load} />

      {nc.status === "EXECUTEE" && (
        <Card>
          <CardContent className="p-5">
            <p className="text-xs text-[#575A57] uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <PackageCheck className="w-3.5 h-3.5" /> Restock exécuté
            </p>
            <p className="text-sm">
              <strong>{(nc.restock_lot_ids || []).length}</strong> lot(s) créé(s) au dépôt central Conthey ·
              <strong> {(nc.restock_movement_ids || []).length}</strong> mouvement(s) HACCP tracé(s).
            </p>
          </CardContent>
        </Card>
      )}

      {canSend && (
        <Card>
          <CardContent className="p-5">
            <p className="text-xs text-[#575A57] uppercase tracking-wide mb-2">Notes internes (non affichées au client)</p>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              data-testid="cn-notes"
            />
            <Button
              onClick={async () => {
                setBusy(true);
                try {
                  await api.put(`/credit-notes/${id}`, { notes });
                  toast.success("Notes enregistrées");
                  await load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Erreur");
                } finally { setBusy(false); }
              }}
              disabled={busy || notes === (nc.notes || "")}
              size="sm"
              className="mt-2"
            >
              Enregistrer les notes
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
